import React from 'react';
import { Box, Typography } from '@mui/material';
import { useThemeContext } from '../context/ThemeContext';

function luminance(hex) {
  const raw = String(hex || '').replace('#', '');
  if (raw.length < 6) return 0;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Theme-aware brand lockup for AppBar / drawer.
 * Uses the ZenRentFlow pin mark + gradient wordmark sized for AppBar readability.
 * variant: 'appBar' (on primary) | 'drawer' (on surface)
 */
export default function BrandLockup({ variant = 'appBar', sx = {} }) {
  const { currentPalette } = useThemeContext();
  const colors = currentPalette?.colors || {};
  const primary = colors.primary || '#94E3E3';
  const onAppBar = variant === 'appBar';
  const lightBar = onAppBar && luminance(primary) > 155;

  // Deeper pink→mint gradient reads on cream/light bars; light pastel on dark bars
  const wordmarkGradient = lightBar || !onAppBar
    ? 'linear-gradient(90deg, #d96c8a 0%, #b07a9a 38%, #5a9a96 68%, #166b6b 100%)'
    : 'linear-gradient(90deg, #f7c9d0 0%, #e8d5e0 40%, #b8e8e4 70%, #94e3e3 100%)';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1.5 },
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL || ''}/assets/logo/zenrentflow-logo-icon.png`}
        alt=""
        aria-hidden
        sx={{
          width: { xs: 28, sm: 42 },
          height: { xs: 28, sm: 42 },
          flexShrink: 0,
          objectFit: 'contain',
          filter: 'drop-shadow(0 1px 2px rgba(38, 50, 56, 0.18))',
        }}
      />

      <Typography
        component="span"
        noWrap
        sx={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          fontWeight: 700,
          fontSize: { xs: '1.05rem', sm: '1.45rem' },
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          backgroundImage: wordmarkGradient,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
          // Soft plate behind glyphs so gradient stays readable on busy AppBar colors
          textShadow: lightBar || !onAppBar ? 'none' : '0 1px 2px rgba(0,0,0,0.25)',
        }}
      >
        ZenRentFlow
      </Typography>
    </Box>
  );
}
